import { useContext, useEffect, useState, useRef } from 'react';
import { AppContext } from '../application/provider';
import { Container } from 'reactstrap';
import LogoPerfil from '../img/logo_perfil.webp';
import PerfilService from '../axios_services/perfil.service';
import ArchivosService from '../axios_services/archivos.service';
import Loader from '../components/Loader/Loader';
import ButtonUpdate from '../components/Buttons/ButtonUpdate';
import './Perfil.css';

function Perfil() {
    const [state] = useContext(AppContext);
    const [perfilData, setPerfilData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [firmaDigital, setFirmaDigital] = useState(null);
    const [loadingFirma, setLoadingFirma] = useState(true);
    const [uploadingFirma, setUploadingFirma] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const fetchPerfilData = async () => {
            try {
                setLoading(true);
                const data = await PerfilService.getDetallePerfil(state.user?.usuario_id);
                setPerfilData(data);
            } catch (error) {
                console.error('Error al cargar datos del perfil:', error);
            } finally {
                setLoading(false);
            }
        };

        if (state.user?.usuario_id) {
            fetchPerfilData();
        } else {
            setLoading(false);
        }
    }, [state.user?.usuario_id]);

    useEffect(() => {
        const fetchFirmaDigital = async () => {
            if (!state.user?.usuario_id) return;

            try {
                setLoadingFirma(true);
                const data = await ArchivosService.getFirmaDigitalConvenio(state.user.usuario_id);
                setFirmaDigital(data);
            } catch (error) {
                console.error('Error al cargar firma digital:', error);
                setFirmaDigital(null);
            } finally {
                setLoadingFirma(false);
            }
        };

        fetchFirmaDigital();
    }, [state.user?.usuario_id]);

    const toTitleCase = (str) => {
        if (!str) return '';
        return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
    };

    const formatBirthday = (fechaNacimiento) => {
        if (!fechaNacimiento) return 'No especificado';

        const meses = [
            'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
            'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
        ];

        const fecha = new Date(fechaNacimiento);
        const dia = fecha.getDate();
        const mes = meses[fecha.getMonth()];

        return `${dia} de ${mes}`;
    };

    const handleUpdateFirmaClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!validTypes.includes(file.type)) {
            alert('Por favor, selecciona un archivo de imagen válido (JPEG, PNG, JPG)');
            return;
        }

        if (file.size > 2000000) {
            alert('El archivo no debe superar los 2MB');
            return;
        }

        try {
            setUploadingFirma(true);
            await ArchivosService.guardarFirmaDigitalConvenio(
                file,
                state.user.usuario_id,
                state.user.usuario_id
            );

            const updatedFirma = await ArchivosService.getFirmaDigitalConvenio(state.user.usuario_id);
            setFirmaDigital(updatedFirma);
            alert('Firma digital actualizada exitosamente');
        } catch (error) {
            console.error('Error al subir firma digital:', error);
            alert('Error al subir la firma digital. Por favor, intenta nuevamente.');
        } finally {
            setUploadingFirma(false);
            event.target.value = '';
        }
    };

    if (loading || loadingFirma || uploadingFirma) {
        return <Loader />;
    }

    return (
        <Container>
            <div className="flex items-center justify-between">
                <h1>CONFIGURACIÓN DE CUENTA</h1>
            </div>

            <div className="perfil-content">
                <div className="perfil-info-section">
                    <div className="perfil-image-wrapper">
                        <img
                            src={LogoPerfil}
                            alt="Profile"
                            className="perfil-image"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                                e.target.parentNode.innerHTML = '<div class="perfil-image-fallback"><svg class="w-20 h-20 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg></div>';
                            }}
                        />
                    </div>
                    <div className="perfil-name-wrapper">
                        <h2 className="perfil-name">{toTitleCase(perfilData?.nombre_completo) || 'Usuario'}</h2>
                        <p className="perfil-cargo">{toTitleCase(perfilData?.cargo) || 'Cargo no especificado'}</p>
                    </div>
                </div>

                <div className="perfil-sections-row">
                    <div className="perfil-section">
                        <div className="perfil-section-header">
                            <h3 className="perfil-section-title">Herramientas</h3>
                            <div className="perfil-section-line"></div>
                        </div>
                        <div className="perfil-tool-item">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <span className="perfil-tool-name">Firma Digital</span>
                                <ButtonUpdate
                                    onClick={handleUpdateFirmaClick}
                                    disabled={uploadingFirma}
                                />
                            </div>

                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/jpeg,image/png,image/jpg"
                                style={{ display: 'none' }}
                            />

                            {firmaDigital?.url_completa ? (
                                <div style={{ marginTop: '15px', textAlign: 'center' }}>
                                    <img
                                        src={firmaDigital.url_completa}
                                        alt="Firma Digital"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '200px',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            padding: '10px',
                                            backgroundColor: '#fff'
                                        }}
                                    />
                                </div>
                            ) : (
                                <div style={{
                                    marginTop: '15px',
                                    padding: '20px',
                                    textAlign: 'center',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    border: '1px dashed #dee2e6'
                                }}>
                                    <p style={{ margin: 0, color: '#6c757d' }}>
                                        Firma digital no registrado.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="perfil-section">
                        <div className="perfil-section-header">
                            <h3 className="perfil-section-title">Acerca de</h3>
                            <div className="perfil-section-line"></div>
                        </div>

                        <div className="perfil-info-group">
                            <h4 className="perfil-info-subtitle">Información de Contacto</h4>
                            <div className="perfil-info-item">
                                <span className="perfil-info-label">Área:</span>
                                <span className="perfil-info-value">{perfilData?.area || 'Área no especificada'}</span>
                            </div>
                            <div className="perfil-info-item">
                                <span className="perfil-info-label">Celular:</span>
                                <span className="perfil-info-value">{perfilData?.celular || 'No especificado'}</span>
                            </div>
                            <div className="perfil-info-item">
                                <span className="perfil-info-label">Correo:</span>
                                <span className="perfil-info-value">{perfilData?.correo_personal || 'No especificado'}</span>
                            </div>
                        </div>

                        <div className="perfil-info-group">
                            <h4 className="perfil-info-subtitle">Información Básica</h4>
                            <div className="perfil-info-item">
                                <span className="perfil-info-label">DNI:</span>
                                <span className="perfil-info-value">{perfilData?.dni || 'No especificado'}</span>
                            </div>
                            <div className="perfil-info-item">
                                <span className="perfil-info-label">Cumpleaños:</span>
                                <span className="perfil-info-value">{formatBirthday(perfilData?.fecha_nacimiento)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    );
}

export default Perfil;